import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MarketingObject } from 'common/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import moment from 'moment-timezone';
import { notUndefined } from 'common/utils/common';
import useShopFormat from 'hooks/useShopFormat';
import { useTranslation } from 'services/localization/useTranslation';
import { GridEnrichedColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import { chain } from 'lodash';
import { Tooltip, Typography } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import MarketingListToolbar from './MarketingListToolbar';
import { httpsCallable } from 'common/frontend/firebase/functions';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';

const MarketingList = () => {
	const { localFormat } = useShopFormat();
	const [marketingError, setMarketingError] = useState<boolean>(false);
	const [marketingLoading, setMarketingLoading] = useState<boolean>(true);
	const [marketingList, setMarketingList] = useState<MarketingObject[]>([]);

	const { t, getTranslation } = useTranslation();
	const shop = useSelector(ShopSelectors.activeShopData);
	const categoriesById = useSelector(StockSelectors.categoriesById);
	const stockProductsById = useSelector(StockSelectors.stockProductsById);
	const shopId = shop?.shopInfo.id;
	const apiRef = useGridApiRef();

	useEffect(() => {
		const fetchMarketingList = async (shopId: string) => {
			setMarketingLoading(true);
			setMarketingError(false);
			try {
				const getMarketingEmailList = httpsCallable<any, any>('admin-getMarketingEmailList');
				const response = await getMarketingEmailList(shopId);
				const marketingData = response.data;
				setMarketingLoading(false);
				marketingData.sort((a: MarketingObject, b: MarketingObject) => {
					if (moment(a.date).isBefore(moment(b.date))) {
						return 1;
					}
					if (moment(b.date).isBefore(a.date)) {
						return -1;
					}
					return 0;
				});
				setMarketingList(marketingData);
			} catch (e) {
				setMarketingLoading(false);
				setMarketingError(true);
			}
		};
		if (shopId) {
			fetchMarketingList(shopId);
		}
	}, [shopId]);

	const columns: GridEnrichedColDef[] = [
		{
			field: 'firstName',
			headerName: t('common:form.firstName'),
			minWidth: 150,
		},
		{
			field: 'lastName',
			headerName: t('common:form.lastName'),
			minWidth: 200,
		},
		{
			field: 'email',
			headerName: t('common:form.email'),
			minWidth: 200,
		},
		{
			field: 'categoryIds',
			headerName: t('common:marketing.categories'),
			valueGetter: (params) => {
				return chain((params.row[params.field] as string[]) ?? [])
					.map((id) => categoriesById[id])
					.filter(notUndefined)
					.map((c) => getTranslation(c.name))
					.sort()
					.value()
					.join(', ');
			},
			minWidth: 200,
			renderCell: (params) => {
				return (
					<Tooltip title={<Typography variant="body2">{params.value}</Typography>}>
						<span>{params.value}</span>
					</Tooltip>
				);
			},
		},
		{
			field: 'stockProductIds',
			headerName: t('common:products.products'),
			valueGetter: (params) => {
				return chain((params.row[params.field] as string[]) ?? [])
					.map((id) => stockProductsById[id])
					.filter(notUndefined)
					.map((p) => getTranslation(p.name))
					.sort()
					.value()
					.join(', ');
			},
			minWidth: 200,
			renderCell: (params) => {
				return (
					<Tooltip title={<Typography variant="body2">{params.value}</Typography>}>
						<span>{params.value}</span>
					</Tooltip>
				);
			},
		},
		{
			field: 'totalShoppers',
			headerName: t('reports.marketing.shoppersInRental'),
			minWidth: 200,
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'date',
			headerName: t('common:times.date', 'Date'),
			valueFormatter: (params) => {
				return localFormat(moment(params.value as string), 'DD.MM.YYYY');
			},
			minWidth: 150,
			align: 'right',
			headerAlign: 'right',
		},
	];

	return (
		<ContainerBox padding="none">
			<RentleDataGrid
				autoHeight
				apiRef={apiRef}
				rows={marketingList}
				columns={columns}
				getRowId={(row) => row.rentalId}
				components={{
					Toolbar: MarketingListToolbar,
				}}
				pagination
				error={marketingError ? true : undefined}
				loading={marketingLoading}
			/>
		</ContainerBox>
	);
};

export default MarketingList;
